import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { centsEcho, parseDollars, parseOptionalDollars } from './money';
import { usePublishPageTitle } from './PageTitle';
import { statusLabel, statusTone, type StatusDomain, type Tone } from './status';

/**
 * The kit: the pieces every Nexus screen is built from.
 *
 * Small on purpose. Each of these exists because the same thing was being
 * written differently on three pages — a card, a status pill, a label over an
 * input, an empty state — and the drift is what made twenty-three screens look
 * like twenty-three products. None of them take a colour: the tone comes from
 * `status.ts`, and a screen that wants a new colour has to change the system.
 */

/* ------------------------------------------------------------------ */
/* Card                                                                */
/* ------------------------------------------------------------------ */

export function Card({
  children,
  title,
  eyebrow,
  sub,
  aside,
  label,
  variant,
  tight,
  id,
  as: Element = 'section',
  headingLevel = 2,
}: {
  children?: React.ReactNode;
  /** Rendered as the card's heading, and as its accessible name. */
  title?: React.ReactNode;
  eyebrow?: React.ReactNode;
  sub?: React.ReactNode;
  /** Badges or a button that belong beside the title. */
  aside?: React.ReactNode;
  /** An explicit accessible name, when the title is not a plain string. */
  label?: string;
  variant?: 'feature' | 'dimmed';
  tight?: boolean;
  /** A fragment target, for a sentence elsewhere that says "go to the X". */
  id?: string;
  as?: 'section' | 'article' | 'div';
  headingLevel?: 2 | 3;
}): React.JSX.Element {
  const headingId = useId();
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  const named = label ?? (typeof title === 'string' ? title : undefined);

  return (
    <Element
      id={id}
      className={`card${variant ? ` ${variant}` : ''}${tight ? ' tight' : ''}`}
      {...(named
        ? { 'aria-label': named }
        : title
          ? { 'aria-labelledby': headingId }
          : {})}
    >
      {(title || aside || eyebrow) && (
        <header className="card-head">
          {eyebrow && <p className="eyebrow">{eyebrow}</p>}
          <div className="card-title-row">
            {title && <Heading id={headingId}>{title}</Heading>}
            {aside}
          </div>
        </header>
      )}
      {sub && <p className="card-sub">{sub}</p>}
      {children}
    </Element>
  );
}

/* ------------------------------------------------------------------ */
/* Page header                                                         */
/* ------------------------------------------------------------------ */

export function PageHeader({
  eyebrow,
  title,
  lead,
  actions,
  hero,
}: {
  eyebrow?: React.ReactNode;
  /** The `<h1>`. One per page, and it says what the page is. */
  title: React.ReactNode;
  /** One line on what the page is for, in the reader's own terms. */
  lead?: React.ReactNode;
  actions?: React.ReactNode;
  /**
   * The landing-page treatment: the marketing site's type scale, its eyebrow
   * pill, and the actions under the sentence instead of across from it.
   *
   * ONLY the pages a stranger can land on — the front door, sign in, sign up.
   * An application page that shouts is an application page nobody can scan.
   */
  hero?: boolean;
}): React.JSX.Element {
  /* The shell's topbar says which page this is, and the `<h1>` is the only
     place that knows. A title that is markup rather than a sentence publishes
     nothing and the shell falls back to the nav label. */
  usePublishPageTitle(title);

  return (
    <header className={hero ? 'page-header hero' : 'page-header'}>
      <div className="titles">
        {eyebrow && <p className={hero ? 'eyebrow eyebrow-pill' : 'eyebrow'}>{eyebrow}</p>}
        <h1>{title}</h1>
        {lead && <p className="lead">{lead}</p>}
        {hero && actions && <div className="header-actions">{actions}</div>}
      </div>
      {!hero && actions && <div className="header-actions">{actions}</div>}
    </header>
  );
}

/* ------------------------------------------------------------------ */
/* Badge                                                               */
/* ------------------------------------------------------------------ */

export function Badge({
  children,
  tone = 'neutral',
  plain,
  testId,
}: {
  children: React.ReactNode;
  tone?: Tone;
  plain?: boolean;
  /**
   * A hook for a test that has to tell two identically-worded pills apart.
   *
   * "Paid" is both an earning's state and a submission's, and they appear on
   * the same card; a test that counted the word rather than the pill would
   * pass whichever one had gone wrong.
   */
  testId?: string;
}): React.JSX.Element {
  return (
    <span className={`badge${plain ? ' plain' : ''}`} data-tone={tone} data-testid={testId}>
      {children}
    </span>
  );
}

/**
 * How much of something is gone, as a bar.
 *
 * Used for a campaign's budget, where the number alone ("$2,340 left") does not
 * answer the question a creator is really asking, which is whether it is nearly
 * over. A bar answers it at a glance and the figures stay beside it, because a
 * bar with no numbers is decoration.
 *
 * `role="img"` with a written label rather than `<progress>`: a screen reader
 * announcing "62 percent" would be announcing the bar, and the sentence a
 * person needs is "$2,340 of $6,000 left".
 */
export function Meter({
  value,
  max,
  label,
  tone = 'positive',
}: {
  value: number;
  max: number;
  label: string;
  tone?: Tone;
}): React.JSX.Element {
  /* A budget that has been overspent (a rounding cent, a manual adjustment) is
     a full bar, never a bar wider than its track. */
  const fraction = max <= 0 ? 0 : Math.min(1, Math.max(0, value / max));
  return (
    <div className="meter" role="img" aria-label={label} data-tone={tone}>
      <span className="meter-fill" style={{ width: `${(fraction * 100).toFixed(1)}%` }} />
    </div>
  );
}

/**
 * A status, as a pill: the API's own sentence where there is one, otherwise the
 * enum turned into words. The colour is the family's, never the screen's.
 */
export function StatusPill({
  status,
  domain,
  label,
}: {
  status: string | null | undefined;
  domain?: StatusDomain;
  /** The API's written label, preferred over anything computed here. */
  label?: string | null;
}): React.JSX.Element {
  return (
    <Badge tone={statusTone(status, domain)}>{label ?? statusLabel(status, domain)}</Badge>
  );
}

/* ------------------------------------------------------------------ */
/* Stats                                                               */
/* ------------------------------------------------------------------ */

export function StatGrid({
  children,
  dense,
}: {
  children: React.ReactNode;
  /**
   * Tighter columns, for a strip of plain counts rather than four figures a
   * person compares. `.stats` already fits five across a 1366px content column;
   * `dense` fits six, which is what stops a fifth or sixth tile wrapping onto a
   * row of its own with four empty cells beside it.
   */
  dense?: boolean;
}): React.JSX.Element {
  return <dl className={dense ? 'stats dense' : 'stats'}>{children}</dl>;
}

export function Stat({
  label,
  value,
  note,
  emphasis,
}: {
  label: string;
  value: React.ReactNode;
  /** The sentence under the figure: what it is, or when it changes. */
  note?: React.ReactNode;
  emphasis?: boolean;
}): React.JSX.Element {
  return (
    <div className="stat" data-emphasis={emphasis ? 'true' : undefined}>
      <dt>{label}</dt>
      <dd>
        {value}
        {note && <div className="stat-note">{note}</div>}
      </dd>
    </div>
  );
}

export function Facts({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <dl className="facts">{children}</dl>;
}

export function Fact({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Pills — a filter that is on or off                                  */
/* ------------------------------------------------------------------ */

/**
 * A row of filters, exactly one of which is on.
 *
 * `role="group"` with a name, and `aria-pressed` on each button — NOT a set of
 * radios. The distinction matters: a radio group is one control whose value is
 * part of a form, and this is a list of buttons that each navigate (they write
 * `?category=` and the page reloads its list). A screen reader is told "Music,
 * toggle button, pressed", which is what these are.
 */
export function Pills({
  label,
  children,
}: {
  /** What the group is for, for a screen reader. Never rendered. */
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="pills" role="group" aria-label={label}>
      {children}
    </div>
  );
}

export function Pill({
  children,
  selected,
  count,
  onClick,
}: {
  children: React.ReactNode;
  selected: boolean;
  /**
   * How many things are behind this filter, printed beside its name.
   *
   * It is the difference between a row of guesses and a row of answers: a
   * category pill that says `Gaming 4` tells a creator whether pressing it is
   * worth doing, and a pill with nothing behind it is not rendered at all.
   */
  count?: number;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button type="button" className="pill" aria-pressed={selected} onClick={onClick}>
      <span>{children}</span>
      {/* A real space between the name and the count, so the accessible name is
          "Gaming 2" and not "Gaming2": the name is computed by concatenating the
          text of the descendants, and two adjacent spans have nothing between
          them. */}
      {count !== undefined && ' '}
      {count !== undefined && <span className="pill-count">{count}</span>}
    </button>
  );
}

/**
 * A search box.
 *
 * `type="search"`, which is what gives a phone keyboard its Search key and the
 * browser its clear affordance, and `role="searchbox"` comes with it. The label
 * is a real one — a magnifying glass with a placeholder is a control nobody can
 * name to a screen reader — and `visuallyHiddenLabel` is how a page keeps it
 * without spending a line of height on the word "Search".
 */
export function SearchField({
  label,
  hint,
  visuallyHiddenLabel,
  ...rest
}: {
  label: string;
  hint?: React.ReactNode;
  visuallyHiddenLabel?: boolean;
} & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className="field">
      <label htmlFor={id} className={visuallyHiddenLabel ? 'sr-only' : undefined}>
        {label}
      </label>
      <input id={id} type="search" autoComplete="off" aria-describedby={hintId} {...rest} />
      {hint && (
        <p className="hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Buttons                                                             */
/* ------------------------------------------------------------------ */

type ButtonVariant = 'primary' | 'ghost' | 'danger' | 'quiet';

export function Button({
  variant = 'ghost',
  small,
  className,
  type = 'button',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  small?: boolean;
}): React.JSX.Element {
  return (
    <button
      type={type}
      className={`btn btn-${variant}${small ? ' small' : ''}${className ? ` ${className}` : ''}`}
      {...rest}
    />
  );
}

export function LinkButton({
  className,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>): React.JSX.Element {
  return <button type="button" className={`btn-link${className ? ` ${className}` : ''}`} {...rest} />;
}

export function Actions({
  children,
  end,
}: {
  children: React.ReactNode;
  end?: boolean;
}): React.JSX.Element {
  return <div className={`actions${end ? ' end' : ''}`}>{children}</div>;
}

/* ------------------------------------------------------------------ */
/* Fields                                                              */
/* ------------------------------------------------------------------ */

interface FieldShell {
  label: string;
  hint?: React.ReactNode;
  /** The API's own message, shown under the input that caused it. */
  error?: string | null;
  id?: string;
}

/**
 * A labelled control.
 *
 * The label is a real `<label for>` and the hint is wired with
 * `aria-describedby` rather than living inside the label. That matters: a hint
 * inside the label makes the control's accessible name the whole paragraph,
 * which is what a screen reader then announces on every focus.
 */
export function Field({
  label,
  hint,
  error,
  id,
  children,
}: FieldShell & {
  children: (props: {
    id: string;
    'aria-describedby': string | undefined;
    'aria-invalid': boolean | undefined;
  }) => React.ReactNode;
}): React.JSX.Element {
  const generated = useId();
  const fieldId = id ?? generated;
  const hintId = hint ? `${fieldId}-hint` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      {children({
        id: fieldId,
        'aria-describedby': describedBy,
        'aria-invalid': error ? true : undefined,
      })}
      {hint && (
        <p className="hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="field-error" id={errorId}>
          {error}
        </p>
      )}
    </div>
  );
}

export function TextField({
  label,
  hint,
  error,
  id,
  textarea,
  ...rest
}: FieldShell &
  React.InputHTMLAttributes<HTMLInputElement> &
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { textarea?: boolean }): React.JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} id={id}>
      {(props) =>
        textarea ? (
          <textarea {...props} {...(rest as React.TextareaHTMLAttributes<HTMLTextAreaElement>)} />
        ) : (
          <input {...props} {...(rest as React.InputHTMLAttributes<HTMLInputElement>)} />
        )
      }
    </Field>
  );
}

export function SelectField({
  label,
  hint,
  error,
  id,
  children,
  ...rest
}: FieldShell & React.SelectHTMLAttributes<HTMLSelectElement>): React.JSX.Element {
  return (
    <Field label={label} hint={hint} error={error} id={id}>
      {(props) => (
        <select {...props} {...rest}>
          {children}
        </select>
      )}
    </Field>
  );
}

export function CheckboxField({
  label,
  ...rest
}: { label: React.ReactNode } & React.InputHTMLAttributes<HTMLInputElement>): React.JSX.Element {
  return (
    <label className="check">
      <input type="checkbox" {...rest} />
      <span>{label}</span>
    </label>
  );
}

/**
 * A box that takes dollars and says, underneath it, the integer cents it is
 * about to send.
 *
 * THE ECHO IS THE POINT. `0.3` is thirty cents and `5000` is five hundred
 * thousand of them; both are legitimate things to type into a rate box and a
 * budget box respectively, and an operator cannot tell which one they just
 * typed until something tells them. The line under the field is that
 * something, and it updates as they type.
 *
 * It is `type="text"` on purpose — see `money.ts`. A `number` input silently
 * blanks `5,000.00`, which for an optional field means the cap the operator
 * just typed arrives as "no cap".
 */
export function MoneyField({
  label,
  hint,
  name,
  defaultValue,
  optional,
  blankMeans,
  readOnly,
  required,
  id,
}: {
  label: string;
  hint?: React.ReactNode;
  name: string;
  defaultValue?: string;
  /** Blank is an answer, not an omission. */
  optional?: boolean;
  /** What blank means, said out loud: "no cap", "no fee". */
  blankMeans?: string;
  /**
   * Frozen, not absent. `readOnly` and NOT `disabled`: a disabled input is
   * left out of the form's `FormData` entirely, so a frozen rate would arrive
   * at the submit handler as "no value" and be refused — the campaign could
   * never be saved again.
   */
  readOnly?: boolean;
  required?: boolean;
  id?: string;
}): React.JSX.Element {
  const [raw, setRaw] = useState(defaultValue ?? '');
  /* An empty required box is not a mistake until somebody has been in it. A
     form that opens shouting at every blank field is a form that has taught
     the operator to ignore its red. */
  const [touched, setTouched] = useState(false);
  const parsed = optional ? parseOptionalDollars(raw) : parseDollars(raw);
  const echo = parsed.ok
    ? parsed.cents === null
      ? (blankMeans ?? 'Left blank.')
      : centsEcho(parsed.cents)
    : null;
  const showError = !parsed.ok && (touched || raw.trim() !== '');

  return (
    <Field
      label={label}
      id={id}
      error={showError ? parsed.reason : null}
      hint={
        <>
          {hint}
          {hint ? ' ' : null}
          <span className={echo ? 'tone-positive' : undefined} data-cents-echo="true">
            {echo ?? '\u2014'}
          </span>
        </>
      }
    >
      {(props) => (
        <input
          {...props}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          name={name}
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          onBlur={() => setTouched(true)}
          readOnly={readOnly}
          required={required}
          placeholder="0.00"
        />
      )}
    </Field>
  );
}

export function FieldRow({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="field-row">{children}</div>;
}

/** A named group of fields inside a long form: basics, economics, rules. */
export function FieldGroup({
  legend,
  note,
  children,
}: {
  legend: string;
  note?: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <fieldset className="group">
      <legend>{legend}</legend>
      {note && <p className="group-note">{note}</p>}
      {children}
    </fieldset>
  );
}

/* ------------------------------------------------------------------ */
/* Alerts                                                              */
/* ------------------------------------------------------------------ */

export function Alert({
  tone = 'info',
  children,
  title,
}: {
  tone?: Tone | 'info';
  children: React.ReactNode;
  title?: string;
}): React.JSX.Element {
  /* `alert` interrupts a screen reader, which is right for a refusal and
     wrong for a confirmation; `status` waits for a pause. */
  const role = tone === 'critical' ? 'alert' : 'status';
  /* THE BODY IS WRAPPED, and that is the whole reason this element exists.
     `children` is usually a bare string, and a bare text node is the one thing
     CSS cannot give a measure to — so the sentence inside an alert ran the
     full width of whatever the alert was in: 127 characters at 1366px and 317
     at 2560. The wrapper is what `.alert > .alert-body` in styles.css caps.
     It also carries the alert's own gap, so several children still stack the
     way they did when they were the flex items. */
  return (
    <div className="alert" data-tone={tone} role={role}>
      {title && <strong>{title}</strong>}
      <div className="alert-body">{children}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Empty state                                                         */
/* ------------------------------------------------------------------ */

/**
 * What a screen says when there is nothing on it.
 *
 * No illustration and no icon: one sentence about why it is empty, and the one
 * action that would fill it. "No data" is not an empty state, it is a shrug.
 */
export function EmptyState({
  title,
  children,
  action,
  headingLevel = 3,
}: {
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
  /**
   * `h3` by default, because an empty state usually sits inside a card that
   * already has the `h2`. When it is the whole page — "Nothing submitted yet"
   * under the page's own `h1`, with no card heading in between — it is the
   * `h2`, and skipping a level there is the one place this component can make
   * the document outline lie.
   */
  headingLevel?: 2 | 3;
}): React.JSX.Element {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <div className="empty">
      <Heading>{title}</Heading>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

/**
 * A page that could not load.
 *
 * Every one of these used to end "Reload the page" — an instruction to do by
 * hand the one thing the page is perfectly able to do itself, and on a phone
 * a pull-to-refresh that loses the creator's place. The sentence stays,
 * because it says what is and is not affected; the button is what acts on it.
 *
 * `role="alert"` comes from `Alert`, so a screen reader is interrupted with
 * the refusal rather than left on a page that silently stopped.
 */
export function LoadErrorPage({
  eyebrow,
  title,
  heading,
  children,
  onRetry,
  retrying,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** "Could not load your wallet." — the refusal, as a sentence. */
  heading: string;
  children: React.ReactNode;
  onRetry?: () => void;
  retrying?: boolean;
}): React.JSX.Element {
  return (
    <>
      <PageHeader eyebrow={eyebrow} title={title} />
      <Alert tone="critical" title={heading}>
        {children}
      </Alert>
      {onRetry && (
        <Actions>
          <Button variant="primary" onClick={onRetry} disabled={retrying}>
            {retrying ? 'Trying…' : 'Try again'}
          </Button>
        </Actions>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Skeletons                                                           */
/* ------------------------------------------------------------------ */

export function Skeleton({ lines = 3, tall }: { lines?: number; tall?: boolean }): React.JSX.Element {
  return (
    <div aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <span
          key={i}
          className={`skeleton${tall ? ' tall' : ''}`}
          style={{ width: `${100 - (i % 3) * 14}%` }}
        />
      ))}
    </div>
  );
}

/**
 * The page's loading state: the shape of what is coming, not a spinner.
 *
 * `role="status"` on an off-screen sentence rather than on the blocks, so a
 * screen reader hears "Loading" once instead of reading out twelve empty divs.
 */
export function LoadingPage({
  title,
  eyebrow,
  stats,
}: {
  title: React.ReactNode;
  eyebrow?: React.ReactNode;
  stats?: boolean;
}): React.JSX.Element {
  return (
    <>
      <PageHeader eyebrow={eyebrow} title={title} />
      <p className="sr-only" role="status">
        Loading…
      </p>
      <div className="card">
        {stats && (
          <div className="skeleton-grid">
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
            <span className="skeleton" />
          </div>
        )}
        <Skeleton lines={4} />
      </div>
      <div className="card">
        <Skeleton lines={3} />
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* Modal / confirm                                                     */
/* ------------------------------------------------------------------ */

const FOCUSABLE =
  'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), ' +
  'textarea:not(:disabled), [tabindex]:not([tabindex="-1"])';

/**
 * Every focusable element inside a container, in DOM order — what a Tab trap
 * (this file's `Modal`, the sidebar's mobile drawer) cycles between.
 *
 * Hidden-ness, not geometry: `offsetWidth`/`offsetParent` are 0/null for
 * everything in a DOM with no layout engine, which would empty this list in
 * jsdom and in any headless check that does not paint.
 */
export function focusableIn(container: HTMLElement): HTMLElement[] {
  return [...container.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => {
    if (el.hasAttribute('hidden')) return false;
    const style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  });
}

/**
 * A dialog that holds the keyboard.
 *
 * `aria-modal` is a promise to a screen reader, not an instruction to the
 * browser: Tab still walks straight out of the panel and into the page behind
 * it unless somebody catches it. Escape closes, Tab cycles inside, and focus
 * goes back to whatever opened it — which for `ConfirmButton` is the button
 * that was just pressed, so a cancelled confirmation leaves the keyboard
 * exactly where it was.
 */
export function Modal({
  title,
  children,
  onClose,
  footer,
}: {
  title: string;
  children?: React.ReactNode;
  onClose: () => void;
  footer?: React.ReactNode;
}): React.JSX.Element {
  const headingId = useId();
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const stops = focusableIn(panel.current);
      if (stops.length === 0) {
        event.preventDefault();
        panel.current.focus();
        return;
      }
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const active = document.activeElement;
      /* The panel itself is `tabIndex={-1}`, so focus sitting ON it counts as
         outside the ring of stops: the next Tab goes to the first one. */
      const inside = active !== panel.current && panel.current.contains(active);
      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => {
      window.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, [onClose]);

  /* THROUGH A PORTAL, ONTO THE BODY.

     `.modal-backdrop` is `position: fixed; inset: 0`, which everybody reads as
     "the viewport" — and it was not, because every one of these dialogs is
     rendered inside a `.card`, and a card carries `backdrop-filter: blur(24px)`.
     A filtered element is a containing block for its fixed descendants, so
     `inset: 0` meant the CARD: the scrim covered one card, the rest of the page
     sat unscrimmed and clickable behind an `aria-modal` dialog, and the dialog
     scrolled with the page. On the dark ground that was a dark rectangle over a
     dark page and nobody saw it; the light theme is what made it visible, which
     is the argument for a second theme in one picture.

     The portal takes the backdrop out from under every filtered ancestor, so
     `fixed` means what it says. Nothing else about the dialog changes: the
     focus trap, Escape, and returning focus to the opener are all still this
     component's, and React still delivers events through the portal to the
     tree it was written in. */
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id={headingId}>{title}</h2>
        {children}
        <Actions end>
          <Button onClick={onClose}>Cancel</Button>
          {footer}
        </Actions>
      </div>
    </div>,
    document.body,
  );
}

/**
 * A button whose action cannot be undone, with the confirmation the action
 * deserves. Used for disconnecting an account, revoking a connection, voiding
 * an earning — never for anything a second click would fix.
 */
export function ConfirmButton({
  label,
  title,
  question,
  confirmLabel,
  onConfirm,
  disabled,
  variant = 'danger',
  small,
}: {
  label: string;
  title: string;
  question: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  disabled?: boolean;
  variant?: ButtonVariant;
  small?: boolean;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant={variant} small={small} disabled={disabled} onClick={() => setOpen(true)}>
        {label}
      </Button>
      {open && (
        <Modal
          title={title}
          onClose={() => setOpen(false)}
          footer={
            <Button
              variant={variant}
              onClick={() => {
                setOpen(false);
                onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          }
        >
          <p>{question}</p>
        </Modal>
      )}
    </>
  );
}
